/**
 * MobileFleetBookingsPage — the fleet customer's "Bookings" tab.
 *
 * A list, not a calendar. The desktop page is a month grid because a fleet manager at
 * a desk is choosing a slot against the workshop's occupancy; on a phone the question
 * is "what have I got booked in", and a grid answers that badly on 375px.
 *
 * Two segments: Upcoming (today forward, the default) and Recent (the last 7 days).
 * Upcoming leads because that is what someone opens the app to check.
 *
 * ONE FETCH, TWO SEGMENTS
 * -----------------------
 * Both segments come from a single /fleet/bookings/calendar call spanning the whole
 * window, split client-side. Switching segments is then instant and offline-safe,
 * where a fetch per segment would put a spinner behind a control that looks like a
 * filter on data already on screen.
 *
 * OCCUPIED SLOTS ARE IGNORED HERE
 * -------------------------------
 * The calendar response also carries other customers' bookings as anonymous busy
 * blocks, which the desktop grid uses to grey out full days. A list has nowhere to
 * put them, and the server validates availability on submit regardless.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AlertCircle, CalendarPlus, Car, CheckCircle, Clock, Loader2, Plus, X,
} from 'lucide-react';
import SegmentedControl from './SegmentedControl';
import MobileListScreen from './MobileListScreen';
import useGroupedList from './useGroupedList';
import { BOOKING_STATUS_KEYS, bookingStatusOptions, groupBookingByStatus } from './bookingGroups';
import BottomSheet from './BottomSheet';
import { ListEmpty, ListError, ListLoading } from './ListStates';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
    fetchFleetCalendar,
    fetchFleetServiceCategories,
    fetchFleetServiceProfiles,
    createFleetBooking,
    cancelFleetBooking,
    clearFleetBookingErrors,
    selectFleetMyBookings,
    selectFleetCalendarLoading,
    selectFleetCalendarError,
    selectFleetBookingCreating,
    selectFleetBookingCreateError,
    selectFleetBookingCancellingUuid,
    selectFleetServiceCategories,
    selectFleetServiceProfiles,
} from '../../store/slices/fleetBookingSlice';
import {
    fetchFleetVehicles,
    selectFleetVehicles,
} from '../../store/slices/fleetVehicleSlice';
import { shiftDay, shortDay, today } from '../../utils/dayRange';
import PullToRefresh from './PullToRefresh';

/*
 * How far the single calendar fetch reaches.
 *
 * Backwards is the 7 days the Recent segment shows. Forwards is a quarter — long
 * enough that a service booked months out still appears, short enough that the
 * response stays small on mobile data.
 */
const PAST_DAYS = 7;
const FUTURE_DAYS = 90;

/*
 * Every value of the server's BookingStatus enum. This map had drifted: it carried
 * CONFIRMED and IN_PROGRESS, which do not exist, and was missing JOB_STARTED,
 * VEHICLE_RECEIVED and NO_SHOW, which do — so the three statuses a customer sees while
 * their car is actually in the workshop all fell through to the default grey.
 */
const STATUS_STYLES = {
    SCHEDULED:        'bg-blue-50 text-blue-700',
    JOB_STARTED:      'bg-amber-50 text-amber-700',
    VEHICLE_RECEIVED: 'bg-indigo-50 text-indigo-700',
    COMPLETED:        'bg-gray-100 text-gray-600',
    CANCELLED:        'bg-red-50 text-red-600',
    NO_SHOW:          'bg-red-50 text-red-600',
};

// The tab split lives in bookingGroups.js, shared with the workshop's own booking
// list — the status string and the tab labels have to agree across both screens.

const statusLabel = (s) =>
    (s || '').replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());

// ── Booking row ──────────────────────────────────────────────────────────────

const BookingRow = ({ booking, onCancel, cancelling }) => {
    const [confirming, setConfirming] = useState(false);

    return (
        <li className="border-b border-gray-100 bg-white px-4 py-3">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900">
                        {shortDay(booking.scheduledDate)}
                        {booking.scheduledTime && (
                            <span className="ml-1.5 font-normal text-gray-500">
                                {booking.scheduledTime.slice(0, 5)}
                            </span>
                        )}
                    </p>
                    <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-gray-500">
                        <Car size={11} className="shrink-0" />
                        {booking.vehicleDisplayName || booking.vehicleRego || 'Vehicle'}
                    </p>
                    {booking.description && (
                        <p className="mt-1 line-clamp-2 text-xs text-gray-600">{booking.description}</p>
                    )}
                    <p className="mt-1 font-mono text-[10px] text-gray-400">{booking.bookingCode}</p>
                </div>

                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                    STATUS_STYLES[booking.status] || 'bg-gray-100 text-gray-600'
                }`}>
                    {statusLabel(booking.status)}
                </span>
            </div>

            {/* `cancellable` is decided server-side — a booking can only be cancelled
                while still SCHEDULED. Re-deriving that from the status string here
                would be a second copy of a rule the backend owns. */}
            {booking.cancellable && !confirming && (
                <button type="button" onClick={() => setConfirming(true)}
                        className="mt-2 rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-500">
                    Cancel booking
                </button>
            )}

            {confirming && (
                <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                    <p className="text-xs text-red-800">
                        Cancel this booking for {shortDay(booking.scheduledDate)}?
                    </p>
                    <div className="mt-2 flex justify-end gap-2">
                        <button type="button" onClick={() => setConfirming(false)} disabled={cancelling}
                                className="rounded-lg px-3 py-1.5 text-xs text-gray-600 disabled:opacity-50">
                            Keep it
                        </button>
                        <button type="button" onClick={() => onCancel(booking)} disabled={cancelling}
                                className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">
                            {cancelling && <Loader2 size={11} className="animate-spin" />}
                            Cancel it
                        </button>
                    </div>
                </div>
            )}
        </li>
    );
};

// ── New booking sheet ────────────────────────────────────────────────────────

/**
 * Create a booking from the phone.
 *
 * Deliberately NOT the desktop's NewBookingDialog: that one is driven by a date the
 * user already picked in a month grid, which does not exist here, so the date has to
 * be part of the form. The payload it builds is identical — the package vs ad-hoc
 * branch is the contract the server enforces, and both paths must send exactly one.
 */
const NewBookingSheet = ({
    open, vehicles, serviceCategories, serviceProfiles,
    onClose, onSubmit, submitting, error,
}) => {
    const [form, setForm] = useState(null);
    const [localError, setLocalError] = useState(null);

    useEffect(() => {
        if (!open) return;
        setForm({
            vehicleUuid: vehicles.length === 1 ? vehicles[0].uuid : '',
            // Tomorrow, not today: a workshop cannot realistically take a booking for
            // the next hour, and defaulting to today invites a same-day request that
            // the workshop then has to phone back about.
            scheduledDate: shiftDay(today(), 1),
            scheduledTime: '09:00',
            mode: serviceProfiles.length > 0 ? 'PACKAGE' : 'ADHOC',
            serviceProfileUuid: '',
            serviceType: '',
            description: '',
        });
        setLocalError(null);
    }, [open, vehicles, serviceProfiles]);

    if (!open || !form) return null;

    const set = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));

    const submit = () => {
        setLocalError(null);
        if (!form.vehicleUuid) return setLocalError('Choose which vehicle to book in.');
        if (!form.scheduledDate) return setLocalError('Pick a date.');

        const isPackage = form.mode === 'PACKAGE';
        if (isPackage && !form.serviceProfileUuid) return setLocalError('Choose a service package.');
        if (!isPackage && !form.serviceType) return setLocalError('Choose what service you need.');

        onSubmit({
            vehicleUuid: form.vehicleUuid,
            scheduledDate: form.scheduledDate,
            scheduledTime: form.scheduledTime || null,
            // Exactly one path — the server rejects a request carrying both or neither.
            ...(isPackage
                ? { serviceProfileUuid: form.serviceProfileUuid }
                : { serviceTypes: [{ type: form.serviceType }] }),
            description: form.description.trim() || null,
        });
    };

    const inputCls = 'w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm';

    return (
        <BottomSheet
            open={open}
            title="Book a service"
            onClose={onClose}
            busy={submitting}
            footer={
                <button type="button" onClick={submit} disabled={submitting}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 text-sm font-semibold text-white disabled:opacity-60">
                    {submitting && <Loader2 size={15} className="animate-spin" />}
                    {submitting ? 'Booking…' : 'Request booking'}
                </button>
            }
        >
                <div className="space-y-3">
                    <div>
                        <label htmlFor="bk-vehicle" className="mb-1 block text-xs font-medium text-gray-600">
                            Vehicle
                        </label>
                        <select id="bk-vehicle" value={form.vehicleUuid}
                                onChange={set('vehicleUuid')} className={inputCls}>
                            <option value="">Select a vehicle…</option>
                            {vehicles.map((v) => (
                                <option key={v.uuid} value={v.uuid}>
                                    {v.displayName || v.regoNumber}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label htmlFor="bk-date" className="mb-1 block text-xs font-medium text-gray-600">
                                Date
                            </label>
                            {/* min stops a booking in the past, which the server rejects
                                anyway — better to make it unpickable than explain it after. */}
                            <input id="bk-date" type="date" value={form.scheduledDate}
                                   min={today()} onChange={set('scheduledDate')} className={inputCls} />
                        </div>
                        <div>
                            <label htmlFor="bk-time" className="mb-1 block text-xs font-medium text-gray-600">
                                Time
                            </label>
                            <input id="bk-time" type="time" value={form.scheduledTime}
                                   onChange={set('scheduledTime')} className={inputCls} />
                        </div>
                    </div>

                    {/* The package/ad-hoc switch only appears when the workshop actually
                        publishes packages. Otherwise it is a toggle with one usable side. */}
                    {serviceProfiles.length > 0 && (
                        <SegmentedControl
                            ariaLabel="Booking type"
                            options={[
                                { key: 'PACKAGE', label: 'Package' },
                                { key: 'ADHOC',   label: 'Single service' },
                            ]}
                            value={form.mode}
                            onChange={(mode) => setForm((p) => ({ ...p, mode }))}
                        />
                    )}

                    {form.mode === 'PACKAGE' ? (
                        <div>
                            <label htmlFor="bk-profile" className="mb-1 block text-xs font-medium text-gray-600">
                                Service package
                            </label>
                            <select id="bk-profile" value={form.serviceProfileUuid}
                                    onChange={set('serviceProfileUuid')} className={inputCls}>
                                <option value="">Select a package…</option>
                                {serviceProfiles.map((p) => (
                                    <option key={p.uuid} value={p.uuid}>{p.name}</option>
                                ))}
                            </select>
                        </div>
                    ) : (
                        <div>
                            <label htmlFor="bk-service" className="mb-1 block text-xs font-medium text-gray-600">
                                Service
                            </label>
                            <select id="bk-service" value={form.serviceType}
                                    onChange={set('serviceType')} className={inputCls}>
                                <option value="">Select a service…</option>
                                {serviceCategories.map((c) => (
                                    <option key={c.value} value={c.value}>{c.label}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div>
                        <label htmlFor="bk-notes" className="mb-1 block text-xs font-medium text-gray-600">
                            Notes <span className="font-normal text-gray-400">(optional)</span>
                        </label>
                        <textarea id="bk-notes" rows={3} value={form.description}
                                  onChange={set('description')}
                                  placeholder="Anything the workshop should know"
                                  className={inputCls} />
                    </div>

                    {(localError || error) && (
                        <div className="flex items-start gap-2 rounded-xl border border-red-100 bg-red-50 p-3">
                            <AlertCircle size={14} className="mt-0.5 shrink-0 text-red-500" />
                            <p className="text-xs text-red-600">{localError || error}</p>
                        </div>
                    )}
                </div>
        </BottomSheet>
    );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const MobileFleetBookingsPage = () => {
    const dispatch = useAppDispatch();

    const myBookings = useAppSelector(selectFleetMyBookings);
    const loading = useAppSelector(selectFleetCalendarLoading);
    const error = useAppSelector(selectFleetCalendarError);
    const creating = useAppSelector(selectFleetBookingCreating);
    const createError = useAppSelector(selectFleetBookingCreateError);
    const cancellingUuid = useAppSelector(selectFleetBookingCancellingUuid);
    const serviceCategories = useAppSelector(selectFleetServiceCategories);
    const serviceProfiles = useAppSelector(selectFleetServiceProfiles);
    const vehicles = useAppSelector(selectFleetVehicles);

    const [segment, setSegment] = useState('open');
    const [sheetOpen, setSheetOpen] = useState(false);
    const [created, setCreated] = useState(null);

    const load = useCallback(() => {
        const now = today();
        dispatch(fetchFleetCalendar({
            startDate: shiftDay(now, -PAST_DAYS),
            endDate: shiftDay(now, FUTURE_DAYS),
        }));
    }, [dispatch]);

    useEffect(() => {
        load();
        // The booking form's dropdowns. Fetched with the list rather than when the
        // sheet opens, so tapping + shows a usable form instead of three spinners.
        dispatch(fetchFleetVehicles({ page: 0, size: 100 }));
        dispatch(fetchFleetServiceCategories());
        dispatch(fetchFleetServiceProfiles());
    }, [dispatch, load]);

    /*
     * Split by STATUS — completed or not. This replaced a split by date (Upcoming /
     * Recent), which answered a different question: a customer checking the app wants
     * to know what is still outstanding, and a booking dated last Tuesday that was
     * never completed belongs with the outstanding ones, not filed under history.
     *
     * The partition is deliberately total. COMPLETED is done; everything else —
     * SCHEDULED, JOB_STARTED, VEHICLE_RECEIVED, and also CANCELLED and NO_SHOW — is
     * "not completed", which is literally true and means every booking appears in
     * exactly one tab and none can go missing. Each card still shows its own status
     * pill, so a cancelled booking is not mistaken for a live one.
     *
     * Both lists are newest first. Comparing ISO date strings works because they are
     * fixed-width and zero-padded — no Date parsing, and no timezone to get wrong.
     */
    const { groups, counts } = useGroupedList(myBookings, {
        keys: BOOKING_STATUS_KEYS,
        groupBy: groupBookingByStatus,
        sortBy: (b) => b.scheduledDate,
    });

    const shown = groups[segment];

    const submit = async (payload) => {
        const result = await dispatch(createFleetBooking(payload));
        if (createFleetBooking.fulfilled.match(result)) {
            setSheetOpen(false);
            setCreated(result.payload);
            // Land on Not completed — a booking that was just made is by definition
            // not completed, and leaving the user on Completed would look like it had
            // not been created.
            setSegment('open');
            load();
        }
    };

    const cancel = async (booking) => {
        const result = await dispatch(cancelFleetBooking(booking.uuid));
        if (cancelFleetBooking.fulfilled.match(result)) load();
    };

    const openSheet = () => {
        dispatch(clearFleetBookingErrors());
        setSheetOpen(true);
    };

    return (
        <MobileListScreen
            title="Bookings"
            action={(
                <button type="button" onClick={openSheet} aria-label="New booking"
                        className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-3 py-2 text-xs font-semibold text-white">
                    <Plus size={15} /> Book
                </button>
            )}
            filters={[{
                ariaLabel: 'Booking status',
                options: bookingStatusOptions(counts),
                value: segment,
                onChange: setSegment,
            }]}
            /* Confirmation of the last booking, dismissible. A booking that vanishes
               into a list is hard to distinguish from one that failed. */
            banner={created && (
                <div className="flex shrink-0 items-center gap-2 border-b border-emerald-100 bg-emerald-50 px-4 py-2.5">
                    <CheckCircle size={15} className="shrink-0 text-emerald-600" />
                    <p className="flex-1 text-xs text-emerald-800">
                        Booked for {shortDay(created.scheduledDate)}
                        {created.bookingCode && ` · ${created.bookingCode}`}
                    </p>
                    <button type="button" onClick={() => setCreated(null)} aria-label="Dismiss"
                            className="rounded p-1 text-emerald-600">
                        <X size={14} />
                    </button>
                </div>
            )}
            loading={loading}
            error={error}
            onRefresh={load}
            items={shown}
            renderItem={(b) => (
                <BookingRow
                    key={b.uuid}
                    booking={b}
                    onCancel={cancel}
                    cancelling={cancellingUuid === b.uuid}
                />
            )}
            empty={{
                icon: segment === 'open' ? CalendarPlus : Clock,
                title: segment === 'open' ? 'Nothing outstanding' : 'Nothing completed yet',
                action: segment === 'open' && (
                    <button type="button" onClick={openSheet}
                            className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2.5 text-xs font-semibold text-white">
                        <Plus size={14} /> Book a service
                    </button>
                ),
            }}
        >
            <NewBookingSheet
                open={sheetOpen}
                vehicles={vehicles || []}
                serviceCategories={serviceCategories || []}
                serviceProfiles={serviceProfiles || []}
                onClose={() => setSheetOpen(false)}
                onSubmit={submit}
                submitting={creating}
                error={createError}
            />
        </MobileListScreen>
    );
};

export default MobileFleetBookingsPage;
